import React, { useEffect, useRef, useState } from 'react';
import { AiDiagnosisEntry } from '../services/AiDiagnosticsService';
import './AiDiagnosisPanel.scss';

interface AiDiagnosisButtonProps {
    diagnosticCode: string;
    diagnosticDescription: string;
    stage: string;
    entry?: AiDiagnosisEntry;
}

/**
 * PSTriage ORCA (Studio) executions link for the stage. PSTriage registers its ORCA client as
 * `PSTriageOrchestrationService-<stage>`, on the ORCA PROD stage for prod and the ORCA BETA stage
 * for everything else (PSOrchestrationServiceCDK/lib/app.ts) -- and each ORCA stage has its own
 * Studio host, so the gamma client only resolves on the beta host.
 */
const orcaExecutionsUrl = (stage: string, sessionId?: string): string => {
    const isProd = Boolean(stage) && stage.toLowerCase() === 'prod';
    const client = isProd ? 'PSTriageOrchestrationService-prod' : 'PSTriageOrchestrationService-gamma';
    const host = isProd ? 'us-west-2.studio.orca.amazon.dev' : 'beta.us-west-2.studio.orca.amazon.dev';
    const base = `https://${host}/#/clients/${client}/executions`;
    // Deep-link to the executions query view (last 7 days); the session id identifies this run.
    const range = encodeURIComponent(
        '{"key":"previous-7-days","amount":7,"unit":"day","type":"relative"}');
    const q = `${base}/actions/query?openTimeRange=${range}`;
    return sessionId ? `${q}&search=${encodeURIComponent(sessionId)}` : q;
};

/** Human "3h ago" / "just now" from an ISO timestamp. */
const relativeTime = (iso?: string): string => {
    if (!iso) {
        return 'never';
    }
    const diffMs = Date.now() - new Date(iso).getTime();
    if (Number.isNaN(diffMs)) {
        return 'unknown';
    }
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) {
        return 'just now';
    }
    if (mins < 60) {
        return `${mins}m ago`;
    }
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) {
        return `${hrs}h ago`;
    }
    return `${Math.floor(hrs / 24)}d ago`;
};

/** Minimal, dependency-free markdown-ish rendering: headings (**bold**) and line breaks. */
const renderDiagnosis = (text: string): React.ReactNode =>
    text.split('\n').map((line, i) => {
        const trimmed = line.trim();
        if (!trimmed) {
            return <br key={i} />;
        }
        const isHeading = /^#{1,3}\s/.test(trimmed) || /^\*\*.+\*\*:?$/.test(trimmed);
        const clean = trimmed.replace(/^#{1,3}\s/, '').replace(/\*\*/g, '');
        const isBullet = /^[-*•]\s/.test(trimmed);
        if (isHeading) {
            return <div key={i} className="ai-diag-heading">{clean}</div>;
        }
        if (isBullet) {
            return <div key={i} className="ai-diag-bullet">{clean.replace(/^[-*•]\s/, '')}</div>;
        }
        return <div key={i} className="ai-diag-line">{clean}</div>;
    });

const AiDiagnosisButton: React.FC<AiDiagnosisButtonProps> = ({
    diagnosticCode,
    diagnosticDescription,
    stage,
    entry,
}) => {
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const status = entry?.status;
    const hasDiagnosis = status === 'COMPLETE' && !!entry?.response;
    const full = entry?.response ?? '';

    // Typewriter reveal when the drawer opens on a completed diagnosis.
    useEffect(() => {
        if (open && hasDiagnosis) {
            setTyped('');
            let i = 0;
            const step = Math.max(2, Math.floor(full.length / 400));
            timerRef.current = setInterval(() => {
                i += step;
                setTyped(full.slice(0, i));
                if (i >= full.length && timerRef.current) {
                    clearInterval(timerRef.current);
                }
            }, 12);
        }
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, [open, hasDiagnosis, full]);

    const pillClass = status === 'COMPLETE' ? 'fresh' : status === 'ERROR' ? 'error' : 'pending';
    const pillLabel = status === 'COMPLETE'
        ? `diagnosed ${relativeTime(entry?.generatedAt)}`
        : status === 'ERROR'
            ? 'diagnosis failed'
            : 'diagnosis pending';

    return (
        <>
            <button
                type="button"
                className={`ai-diag-trigger ${status ? status.toLowerCase() : 'none'}`}
                onClick={() => setOpen(true)}
                title="PSTriage AI-driven diagnosis"
                aria-label="Open AI diagnosis"
            >
                <span className="ai-diag-spark" aria-hidden="true">✨</span>
                AI Diagnosis
                {status && status !== 'COMPLETE' && <span className={`ai-diag-dot ${pillClass}`} />}
            </button>

            {open && (
                <div className="ai-diag-overlay" onClick={() => setOpen(false)}>
                    <aside className="ai-diag-drawer" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="AI diagnosis">
                        <header className="ai-diag-header">
                            <div className="ai-diag-title">
                                <span className="ai-diag-spark" aria-hidden="true">✨</span>
                                PSTriage AI Diagnosis
                            </div>
                            <button type="button" className="ai-diag-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
                        </header>

                        <div className="ai-diag-sub">
                            <code className="ai-diag-code">{diagnosticCode}</code>
                            <span className="ai-diag-desc">{diagnosticDescription}</span>
                        </div>

                        <div className={`ai-diag-pill ${pillClass}`}>{pillLabel}</div>

                        <div className="ai-diag-body">
                            {hasDiagnosis && (
                                <div className="ai-diag-content">{renderDiagnosis(typed || full)}</div>
                            )}
                            {status === 'ERROR' && (
                                <div className="ai-diag-state ai-diag-state-error">
                                    {entry?.response || 'AI diagnosis is temporarily unavailable.'}
                                </div>
                            )}
                            {(!status || status === 'IN_PROGRESS') && (
                                <div className="ai-diag-state ai-diag-state-pending">
                                    <div className="ai-diag-shimmer" />
                                    <div className="ai-diag-shimmer short" />
                                    <div className="ai-diag-shimmer" />
                                    <p className="ai-diag-pending-note">
                                        PSTriage hasn’t diagnosed this bucket yet. The backend runs diagnoses on each
                                        data refresh — check back shortly.
                                    </p>
                                </div>
                            )}
                        </div>

                        {entry && (entry.sampleRefundIds?.length || entry.agentSessionId) && (
                            <div className="ai-diag-provenance">
                                {entry.sampleRefundIds && entry.sampleRefundIds.length > 0 && (
                                    <details className="ai-diag-prov-item">
                                        <summary>Analyzed {entry.sampleRefundIds.length} sample refunds</summary>
                                        <ul className="ai-diag-sample-list">
                                            {entry.sampleRefundIds.map((id) => (
                                                <li key={id} className="ai-diag-sample-id">{id}</li>
                                            ))}
                                        </ul>
                                    </details>
                                )}
                                {entry.agentSessionId && (
                                    <div className="ai-diag-prov-item">
                                        <span className="ai-diag-prov-label">PSTriage session</span>
                                        <code className="ai-diag-prov-value">{entry.agentSessionId}</code>
                                    </div>
                                )}
                                <a
                                    className="ai-diag-orca-link"
                                    href={orcaExecutionsUrl(stage, entry.agentSessionId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    View PSTriage workflow in ORCA ↗
                                </a>
                            </div>
                        )}

                        {entry?.history && entry.history.length > 0 && (
                            <div className="ai-diag-history">
                                <button type="button" className="ai-diag-history-toggle" onClick={() => setShowHistory((s) => !s)}>
                                    {showHistory ? 'Hide' : 'Show'} history ({entry.history.length})
                                </button>
                                {showHistory && (
                                    <ol className="ai-diag-timeline">
                                        {entry.history.map((h, i) => (
                                            <li key={i} className="ai-diag-timeline-item">
                                                <div className="ai-diag-timeline-time">{relativeTime(h.generatedAt)}</div>
                                                <div className="ai-diag-timeline-text">{renderDiagnosis(h.response)}</div>
                                            </li>
                                        ))}
                                    </ol>
                                )}
                            </div>
                        )}
                    </aside>
                </div>
            )}
        </>
    );
};

export default AiDiagnosisButton;
